import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { ExclusionWindow, PrivilegeTypes } from '@bringg/types';
import { ConfirmModal, Notification } from '@bringg/react-components';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';
import { downloadFile, isOneTimeFrameBetweenOtherTimeFrame } from '@bringg-frontend/utils';
import moment from 'moment-timezone';

import ExclusionWindowsCsvImport from './exclusion-windows-csv-import/exclusion-windows-csv-import';
import { useStores } from 'bringg-web/recipes';
import PlannedDeliveryWindowsToolbar, {
	DropdownData
} from 'bringg-web/features/planned-delivery-windows/toolbar/planned-delivery-windows-toolbar';
import ExclusionWindowModal from 'bringg-web/features/planned-delivery-windows/modals/exclusion-window-modal/exclusion-window-modal';
import ExclusionWindowTable from 'bringg-web/features/planned-delivery-windows/exclusion-windows/exclusion-windows-table';
import useTimeFormat from 'bringg-web/hooks/use-time-format';
import { NO_TEAM } from 'bringg-web/features/planned-delivery-windows/planned-delivery-windows-view';
import useDateTimeFormat from 'bringg-web/hooks/use-date-time-format';
import { useHasAccess } from 'bringg-web/utils/privileges';
import { timezoneProvider } from 'bringg-web/services/timezone/timezone-provider';
import { EXCLUSION_WINDOWS_CSV_IMPORT_FF } from '../constants';
import { useEnableImprovedExclusionWindowsUx } from './use-enable-improved-exclusion-windows-ux';
import { exclusionWindowsToCSV } from 'bringg-web/features/planned-delivery-windows/exclusion-windows/exclusion-windows-export-utils';

import styles from './exclusion-window-view.module.scss';

interface Props {
	setTeamInUrl: (teamId: number) => void;
	selectedTeamId: number;
}

const EXPORT_CSV_FILE_NAME = 'blackouts.csv';

const ExclusionWindowView = ({ setTeamInUrl, selectedTeamId }: Props) => {
	const [exclusionModalVisible, setExclusionModalVisible] = useState(false);
	const [importModalOpen, setImportModalOpen] = useState(false);
	const [exclusionWindowsList, setExclusionWindowsList] = useState<ExclusionWindow[]>([]);
	const [exclusionWindowToEdit, setExclusionWindowToEdit] = useState<ExclusionWindow>(null);
	const [exclusionWindowTableLoader, setExclusionWindowTableLoader] = useState(false);
	const [calendarRangeDates, setCalendarRangeDates] = useState<{ startDate: string; endDate: string }>({
		startDate: moment().startOf('day').toISOString(),
		endDate: moment().add(7, 'days').endOf('day').toISOString()
	});
	const { t } = useTranslation();
	const { exclusionWindows, serviceAreasStore } = useStores();
	const dateTimeFormat = useDateTimeFormat();
	const hourFormat = useTimeFormat();
	const fetchExclusionWindowsByTime = useHasFeatureFlag('fetch_exclusion_windows_by_time');
	const hasAccessToEdit = useHasAccess(PrivilegeTypes.EDIT_EXCLUSION_WINDOWS);
	const hasExclusionWindowsCSVImportFF = useHasFeatureFlag(EXCLUSION_WINDOWS_CSV_IMPORT_FF);
	const hasBetterExclusionWindowsUX = useEnableImprovedExclusionWindowsUx();

	const timezone =
		selectedTeamId === NO_TEAM
			? timezoneProvider.getTimezoneByMerchant()
			: timezoneProvider.getTimezoneByTeamId(selectedTeamId);

	const {
		teamsStore: { allCurrentUserTeams, all: allTeams },
		usersStore
	} = useStores();

	const fetchExclusionWindowsAndSetToView = async (
		{ teamId, startTime, endTime }: { teamId: number; startTime: string; endTime: string },
		{ useCache = true }: { useCache?: boolean } = {}
	) => {
		setExclusionWindowTableLoader(true);

		const datesObject = fetchExclusionWindowsByTime
			? {
					start_time: startTime,
					end_time: endTime
			  }
			: {};
		const promises: Promise<ExclusionWindow[]>[] = [
			exclusionWindows.loadAllWithoutTeam(
				{
					...datesObject
				},
				{ useCache }
			)
		];
		if (teamId !== NO_TEAM) {
			promises.push(
				exclusionWindows.loadAllByTeam(
					{
						team_id: teamId,
						...datesObject
					},
					{ useCache }
				)
			);
		}
		const [exclusionByCompany, exclusionByTeam] = await Promise.all(promises);

		setExclusionWindowsList(() => {
			if (exclusionByTeam) {
				return [...exclusionByCompany, ...exclusionByTeam];
			}
			return exclusionByCompany;
		});
		setExclusionWindowTableLoader(false);
	};

	const onExclusionWindowCreated = (createdExclusionWindow: ExclusionWindow[]) => {
		const exclusionWindowsToAdd = createdExclusionWindow.filter(
			exclusionWindow =>
				exclusionWindow.team_id === selectedTeamId ||
				(exclusionWindow.team_id === null && selectedTeamId === NO_TEAM)
		);

		const exclusionWindowsToAddFiltered = exclusionWindowsToAdd.filter(exclusionWindowsToAdd =>
			isOneTimeFrameBetweenOtherTimeFrame(
				[
					moment(exclusionWindowsToAdd.start_time).startOf('day'),
					moment(exclusionWindowsToAdd.end_time).endOf('day')
				],
				[moment(calendarRangeDates.startDate).startOf('day'), moment(calendarRangeDates.endDate).endOf('day')]
			)
		);
		if (exclusionWindowsToAddFiltered.length) {
			setExclusionWindowsList(prevState => [...prevState, ...exclusionWindowsToAddFiltered]);
		}
	};

	const openExclusionWindowModalWithEditMode = (id: number) => {
		const exclusionWindow = exclusionWindowsList.find(ew => ew.id === id);
		setExclusionWindowToEdit(exclusionWindow);
		setExclusionModalVisible(true);
	};

	const onCloseExclusionWindowModal = () => {
		setExclusionModalVisible(false);
		setExclusionWindowToEdit(null);
	};

	const onDeleteOpenConfirmModal = (id: number) => {
		ConfirmModal({
			title: t('EXCLUSION_WINDOW_VIEW_DELETE_CONFIRM_MODAL.ARE_YOU_SURE'),
			okText: t('EXCLUSION_WINDOW_VIEW_DELETE_CONFIRM_MODAL.IM_SURE'),
			cancelText: t('EXCLUSION_WINDOW_VIEW_DELETE_CONFIRM_MODAL.CANCEL'),
			okType: 'danger',
			content: `${t('EXCLUSION_WINDOW_VIEW_CONFIRM_MODAL.ARE_YOU_SURE_WANT_TO_DELETE_EXCLUSION')} ${id}`,
			onOk: async () => {
				try {
					await exclusionWindows.delete(id);
					setExclusionWindowsList(exclusionWindowsList.filter(exclusionWindow => exclusionWindow.id !== id));
					Notification.success(t('EXCLUSION_WINDOW_VIEW_DELETE_CONFIRM_MODAL.SUCCESS_TO_DELETE'));
				} catch (e) {
					Notification.error(t('EXCLUSION_WINDOW_VIEW_DELETE_CONFIRM_MODAL.FAILED_TO_DELETE'));
					console.error('cannot delete exclusion window: ', e);
				}
			}
		});
	};

	const updateLocalState = (id: number) => {
		const foundExclusionWindow = exclusionWindows.get(id);
		setExclusionWindowsList(prevState =>
			prevState.map(item => (item.id === foundExclusionWindow.id ? foundExclusionWindow : item))
		);
	};

	// run only once
	useEffect(() => {
		void fetchExclusionWindowsAndSetToView(
			{ teamId: selectedTeamId, startTime: calendarRangeDates.startDate, endTime: calendarRangeDates.endDate },
			{ useCache: false }
		);
	}, []);

	useEffect(() => {
		if (!hasBetterExclusionWindowsUX) {
			return;
		}

		void serviceAreasStore.fetchServiceAreas();
	}, [hasBetterExclusionWindowsUX, serviceAreasStore]);

	const onTeamChange = (teamId: number) => {
		void fetchExclusionWindowsAndSetToView(
			{ teamId, startTime: calendarRangeDates.startDate, endTime: calendarRangeDates.endDate },
			{ useCache: true }
		);
		setTeamInUrl(teamId);
	};

	const dropdownData: DropdownData = useMemo(() => {
		const items = [
			{
				label: t('EXCLUSION_WINDOWS_MODAL_BUTTON_DROPDOWN.ADD_EXCLUSION_WINDOW'),
				onClick: () => setExclusionModalVisible(true),
				disabled: !hasAccessToEdit
			}
		];

		if (hasExclusionWindowsCSVImportFF) {
			items.push({
				label: t('EXCLUSION_WINDOWS_MODAL_BUTTON_DROPDOWN.IMPORT_EXCLUSION_WINDOW'),
				onClick: () => setImportModalOpen(true),
				disabled: !hasAccessToEdit
			});
		}

		return {
			trigger: ['click'],
			text: t('PDW_ALL_LIST_DROPDOWN.CREATE_BUTTON'),
			items: items.map((el, index) => ({
				...el,
				key: index.toString()
			}))
		};
	}, [hasAccessToEdit, hasExclusionWindowsCSVImportFF, t]);

	const setCalenderDates = (startTime: string, endTime: string) => {
		setCalendarRangeDates({
			startDate: startTime,
			endDate: endTime
		});
		void fetchExclusionWindowsAndSetToView({ teamId: selectedTeamId, startTime, endTime }, { useCache: false });
	};

	return (
		<div className={styles.exclusionWindowsView}>
			<div className={hasBetterExclusionWindowsUX ? styles.allSchedulesToolbarWide : styles.allSchedulesToolbar}>
				<PlannedDeliveryWindowsToolbar
					selectedTeam={selectedTeamId}
					changeCalendarRangeDates={setCalenderDates}
					onTeamChange={onTeamChange}
					dropdownData={dropdownData}
					timezone={timezone}
					isEditAllowed={hasAccessToEdit}
					fetchDataByDate
					calendarRangeDates={calendarRangeDates}
					// TODO: create new endpoint in hagmonia to get all teams for the current user by one request
					onExport={async () => {
						const teamIds = (usersStore.isAdmin ? allTeams : allCurrentUserTeams).map(team => team.id);

						const filter = {
							start_time: moment.tz(timezone).startOf('day').toISOString(),
							end_time: moment.tz(timezone).add(1, 'year').endOf('day').toISOString()
						};

						// error is handled in the toolbar
						const exclusionWindowsToExport = await Promise.all(
							teamIds.map(team_id =>
								exclusionWindows.loadAllByTeam({ team_id, ...filter }, { useCache: false })
							)
						);

						const flattenExclusionWindows = exclusionWindowsToExport.flat();

						if (!flattenExclusionWindows.length) {
							Notification.error(t('EXCLUSION_WINDOWS_MODAL_BUTTON_DROPDOWN.EXPORT_NO_DATA'));
							return;
						}

						downloadFile(
							exclusionWindowsToCSV(flattenExclusionWindows, dateTimeFormat, EXPORT_CSV_FILE_NAME)
						);
					}}
				/>
			</div>
			<div
				className={
					hasBetterExclusionWindowsUX ? styles.exclusionWindowsTableWide : styles.exclusionWindowsTable
				}
			>
				<ExclusionWindowTable
					format={dateTimeFormat}
					selectedTeamId={selectedTeamId}
					exclusionWindows={exclusionWindowsList}
					openExclusionWindowModalWithEditMode={openExclusionWindowModalWithEditMode}
					tableLoading={exclusionWindowTableLoader}
					onDeleteOpenConfirmModal={onDeleteOpenConfirmModal}
					timezone={timezone}
				/>
			</div>

			{exclusionModalVisible && (
				<ExclusionWindowModal
					onCreate={onExclusionWindowCreated}
					onEdit={updateLocalState}
					isVisible={exclusionModalVisible}
					title={
						exclusionWindowToEdit
							? t('EXCLUSION_WINDOWS_MODAL_EDIT_MODE.TITLE')
							: t('EXCLUSION_WINDOWS_MODAL_ADD_MODE.TITLE')
					}
					closeModal={onCloseExclusionWindowModal}
					timeFormat={hourFormat}
					teamId={selectedTeamId}
					exclusionWindow={exclusionWindowToEdit}
				/>
			)}

			<ExclusionWindowsCsvImport
				open={importModalOpen}
				onClose={() => setImportModalOpen(false)}
				onComplete={() =>
					fetchExclusionWindowsAndSetToView(
						{
							teamId: selectedTeamId,
							startTime: calendarRangeDates.startDate,
							endTime: calendarRangeDates.endDate
						},
						{ useCache: false }
					)
				}
			/>
		</div>
	);
};

export default observer(ExclusionWindowView);
